import { useEffect, useMemo, useState } from 'react'
import { adminApi } from '../services/api'
import { useColors } from '../hooks/useColors'
import GeneralSettingsTab from './settings/GeneralSettingsTab'
import AgentSettingsTab from './settings/AgentSettingsTab'
import NotificationSettingsTab from './settings/NotificationSettingsTab'
import PaymentSettingsTab from './settings/PaymentSettingsTab'
import SlotSettingsTab from './settings/SlotSettingsTab'

type TabKey = 'general' | 'agent' | 'notification' | 'payment' | 'slot'

type SettingsPayload = {
  pricing?: Record<string, unknown>
  operations?: Record<string, unknown>
  zones?: Record<string, unknown>
  loyalty?: Record<string, unknown>
  express?: Record<string, unknown>
  general?: Record<string, unknown>
}

const flatten = (payload: SettingsPayload): Record<string, string> => {
  const merged: Record<string, unknown> = {
    ...(payload.general || {}),
    ...(payload.operations || {}),
    ...(payload.pricing || {}),
    ...(payload.loyalty || {}),
    ...(payload.express || {}),
    ...(payload.zones || {}),
  }
  return Object.entries(merged).reduce<Record<string, string>>((acc, [k, v]) => {
    acc[k] = String(v ?? '')
    return acc
  }, {})
}

export default function SettingsPage() {
  const colors = useColors()
  const [activeTab, setActiveTab] = useState<TabKey>('general')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [saved, setSaved] = useState(false)
  const [formData, setFormData] = useState<Record<string, string>>({})

  const load = async () => {
    try {
      setLoading(true)
      setError('')
      const res = await adminApi.getSettings()
      const payload = (res?.data ?? res) as SettingsPayload
      setFormData(flatten(payload || {}))
    } catch {
      setError('Unable to load settings.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { void load() }, [])
  useEffect(() => {
    if (!saved) return
    const id = window.setTimeout(() => setSaved(false), 2000)
    return () => window.clearTimeout(id)
  }, [saved])

  const onChange = (key: string, value: string) => {
    setFormData((prev) => ({ ...prev, [key]: value }))
  }

  const onSave = async () => {
    setSaving(true)
    setError('')
    try {
      await adminApi.updateSettings(formData)
      setSaved(true)
    } catch {
      setError('Failed to save settings.')
    } finally {
      setSaving(false)
    }
  }

  const tabs = useMemo(() => ([
    { key: 'general' as const, label: 'General' },
    { key: 'agent' as const, label: 'Agent' },
    { key: 'notification' as const, label: 'Notification' },
    { key: 'payment' as const, label: 'Payment' },
    { key: 'slot' as const, label: 'Slot' },
  ]), [])

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <h2 className="font-display" style={{ margin: 0, color: colors.textPrimary }}>Settings</h2>
      {error ? <div style={{ color: '#b91c1c' }}>{error}</div> : null}
      <div style={{ display: 'flex', gap: 8 }}>
        {tabs.map((t) => (
          <button key={t.key} onClick={() => setActiveTab(t.key)} style={{ padding: '8px 12px', borderRadius: 10, border: `1px solid ${colors.border}`, background: activeTab === t.key ? colors.textPrimary : colors.surface, color: activeTab === t.key ? colors.surface : colors.textPrimary }}>{t.label}</button>
        ))}
      </div>

      <div style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 12, padding: 14 }}>
        {loading ? <div style={{ color: colors.textMuted }}>Loading settings...</div> : (
          <>
            {activeTab === 'general' ? <GeneralSettingsTab formData={formData} onChange={onChange} /> : null}
            {activeTab === 'agent' ? <AgentSettingsTab formData={formData} onChange={onChange} /> : null}
            {activeTab === 'notification' ? <NotificationSettingsTab formData={formData} onChange={onChange} /> : null}
            {activeTab === 'payment' ? <PaymentSettingsTab formData={formData} onChange={onChange} /> : null}
            {activeTab === 'slot' ? <SlotSettingsTab formData={formData} onChange={onChange} /> : null}
          </>
        )}
      </div>

      <div style={{ display: 'flex', gap: 8 }}>
        <button onClick={() => void onSave()} disabled={saving}>{saving ? 'Saving...' : 'Save Changes'}</button>
        {saved ? <span style={{ color: '#15803d' }}>Saved</span> : null}
      </div>
    </div>
  )
}
